// Types
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import LoadingWrapper from 'components/LoadingWrapper';
// Utils
import { getText } from 'i18n/intl';
import {
  discardEmailPreferences,
  fetchEmailPreferences,
  setUpdatedEmailPreferences,
  updateEmailPreferences,
  updatedEmailPreferencesSelector,
} from 'slices/emailPreferences.slice';
import {
  requestObjectTypes,
  selectObjectTypesList,
} from 'slices/objectTypes.slice';
import { useAppDispatch } from 'slices/store';
import {
  discardChange,
  fetchSystemPreferenceVariables,
  fetchSystemPreferences,
  setUpdatedSystemPreferences,
  systemPreferenceVariablesSelector,
  updateSystemPreferences,
  updatedSystemPreferencesSelector,
} from 'slices/systemPreferences.slice';
import type {
  TEmailPreferences,
  TSystemPreferencesMisc,
} from '@timeedit/types/lib/types';
// Components
import { Button, DefaultContentLayout, TEInput } from '@timeedit/ui-components';
import { Slider, Space, Typography } from '@timeedit/ui-components/antd';
import {
  maxEmailMemberOptions,
  maxImportFieldValuesOptions,
  maxReservationObjectOptions,
} from './PreferenceOption.constants';
// Slices
// Styles
import './index.scss';

const Advanced = () => {
  const dispatch = useAppDispatch();
  const settings: TSystemPreferencesMisc = useSelector(
    updatedSystemPreferencesSelector
  );
  const emailPreferences: TEmailPreferences = useSelector(
    updatedEmailPreferencesSelector
  );
  const types = useSelector(selectObjectTypesList);
  const typeOptions = useMemo(() => {
    return types.map((type) => ({
      label: type.name,
      value: type.id,
    }));
  }, [types]);
  const systemVariables = useSelector(systemPreferenceVariablesSelector);

  useEffect(() => {
    dispatch(fetchSystemPreferences());
    dispatch(requestObjectTypes());
    dispatch(fetchEmailPreferences());
    dispatch(fetchSystemPreferenceVariables());
  }, []);

  const handleGeneralChange = ({
    key,
    value,
    objects,
  }: {
    key?: string;
    value?: any;
    objects?: Record<string, any>;
  }) => {
    const newObject = objects || { [key || '']: value };
    dispatch(
      setUpdatedSystemPreferences({
        ...settings,
        ...newObject,
      })
    );
  };

  const handleEmailChange = ({
    key,
    value,
    objects,
  }: {
    key?: string;
    value?: any;
    objects?: Record<string, any>;
  }) => {
    const newObject = objects || { [key || '']: value };
    dispatch(
      setUpdatedEmailPreferences({
        ...emailPreferences,
        ...newObject,
      })
    );
  };

  return (
    <DefaultContentLayout
      title={getText('advanced_preferences_title')}
      subtitle={getText('advanced_preferences_subtitle')}
      actions={
        <>
          <Button
            onClick={() => {
              dispatch(discardChange());
              dispatch(discardEmailPreferences());
            }}
          >
            {getText('discard_changes')}
          </Button>
          <Button
            type="primary"
            onClick={() => {
              dispatch(updateSystemPreferences(settings));
              dispatch(updateEmailPreferences(emailPreferences));
            }}
          >
            {getText('save_changes')}
          </Button>
        </>
      }
    >
      <LoadingWrapper isLoading={false}>
        <div className="advanced-preferences">
          <div className="content">
            <Typography.Title level={4}>General</Typography.Title>
            <TEInput.Select
              label="Calendar background text"
              tooltip="Calendar background text"
              value={settings.calendarBackgroundText}
              onChange={(val) => {
                handleGeneralChange({
                  key: 'calendarBackgroundText',
                  value: val,
                });
              }}
              options={systemVariables.map((variable) => ({
                label: variable,
                value: variable,
              }))}
            />
            <TEInput.LabelWrapper
              label="Order accept span"
              tooltip="Order accept span"
            >
              <Space>
                <TEInput.InputNumber
                  value={settings.orderAcceptSpanFrom}
                  onChange={(val) => {
                    handleGeneralChange({
                      key: 'orderAcceptSpanFrom',
                      value: val,
                    });
                  }}
                  formatter={(val) => `${val}%`}
                />
                <Slider
                  className="order-accept-span-slider"
                  range
                  value={[
                    settings.orderAcceptSpanFrom,
                    settings.orderAcceptSpanTo,
                  ]}
                  onChange={(val) => {
                    handleGeneralChange({
                      objects: {
                        orderAcceptSpanFrom: val[0],
                        orderAcceptSpanTo: val[1],
                      },
                    });
                  }}
                  style={{ width: 160 }}
                />
                <TEInput.InputNumber
                  value={settings.orderAcceptSpanTo}
                  onChange={(val) => {
                    handleGeneralChange({
                      key: 'orderAcceptSpanTo',
                      value: val,
                    });
                  }}
                  formatter={(val) => `${val}%`}
                />
              </Space>
            </TEInput.LabelWrapper>
            <TEInput.Select
              label="Double booking type"
              tooltip="Double booking type"
              value={settings.doubleBookingType || undefined}
              options={typeOptions}
              onChange={(val) => {
                handleGeneralChange({
                  key: 'doubleBookingType',
                  value: val || 0,
                });
              }}
            />
            <TEInput.Select
              label="Max reservation objects"
              tooltip="Max reservation objects"
              value={settings.maxReservationObjects || undefined}
              options={maxReservationObjectOptions}
              onChange={(val) => {
                handleGeneralChange({
                  key: 'maxReservationObjects',
                  value: val || 0,
                });
              }}
            />
            <TEInput.Select
              label="Max import field values"
              tooltip="Max import field values"
              value={settings.maxImportFieldValues || undefined}
              options={maxImportFieldValuesOptions}
              onChange={(val) => {
                handleGeneralChange({
                  key: 'maxImportFieldValues',
                  value: val || 0,
                });
              }}
            />
            <TEInput.Switch
              label="Optional relations"
              tooltip="Make optional if any object is optional on multi selection"
              value={settings.optionalRelationsAnyObject}
              onChange={(val) => {
                handleGeneralChange({
                  key: 'optionalRelationsAnyObject',
                  value: val,
                });
              }}
            />
            <TEInput.Switch
              label="Reservation exceptions"
              tooltip="Hide possibility of making member exceptions in reservations"
              value={settings.reservationExceptionsHide}
              onChange={(val) => {
                handleGeneralChange({
                  key: 'reservationExceptionsHide',
                  value: val,
                });
              }}
            />
            <TEInput.Switch
              label="Reservation/order list"
              tooltip="Use reserve permission on objects in list when modify permission is used"
              value={settings.useReservePermissionOnObjects}
              onChange={(val) => {
                handleGeneralChange({
                  key: 'useReservePermissionOnObjects',
                  value: val,
                });
              }}
            />
            <TEInput.Switch
              label="Reservation permissions"
              tooltip="Allow non admin users to change reservation organizations"
              value={settings.reservationPermissionsAllowChangeOrganization}
              onChange={(val) => {
                handleGeneralChange({
                  key: 'reservationPermissionsAllowChangeOrganization',
                  value: val,
                });
              }}
            />
            <TEInput.Switch
              label="Affect orders"
              tooltip='Allow user to change "affect orders" on template regardless of reservation count'
              value={settings.affectOrdersAllowChange}
              onChange={(val) => {
                handleGeneralChange({
                  key: 'affectOrdersAllowChange',
                  value: val,
                });
              }}
            />
            <br />
            <Typography.Title level={4}>Email</Typography.Title>
            <TEInput.Select
              label="Max email members"
              tooltip="Max email members"
              value={emailPreferences.emailMaxMembers || undefined}
              options={maxEmailMemberOptions}
              onChange={(val) => {
                handleEmailChange({
                  key: 'emailMaxMembers',
                  value: val || 0,
                });
              }}
            />
            <TEInput.Switch
              label="Set custom email as sender"
              tooltip="Set custom email as sender"
              value={!emailPreferences.useUserEmailAsFrom}
              onChange={(val) => {
                handleEmailChange({
                  key: 'useUserEmailAsFrom',
                  value: !val,
                });
              }}
            />
            <TEInput.Input
              label="Email as sender"
              tooltip="Email as sender"
              value={emailPreferences.fromAddress}
              onChange={(val) => {
                handleEmailChange({
                  key: 'fromAddress',
                  value: val,
                });
              }}
              disabled={emailPreferences.useUserEmailAsFrom}
            />
            <TEInput.Switch
              label='Set user email as "Reply"'
              tooltip='Set user email as "Reply"'
              value={
                emailPreferences.useUserEmailAsFrom
                  ? false
                  : emailPreferences.useUserEmailAsReplyTo
              }
              onChange={(val) => {
                handleEmailChange({
                  key: 'useUserEmailAsReplyTo',
                  value: val,
                });
              }}
              disabled={emailPreferences.useUserEmailAsFrom}
            />
            <TEInput.Switch
              label="Include empty reservation field to email"
              tooltip="Include empty reservation field to email"
              value={emailPreferences.includeEmptyReservationFields}
              onChange={(val) => {
                handleEmailChange({
                  key: 'includeEmptyReservationFields',
                  value: val,
                });
              }}
            />
            <TEInput.Switch
              label="Send slack notification for debug"
              tooltip="Send slack notification for debug"
              value={emailPreferences.sendServerEmailToSlack}
              onChange={(val) => {
                handleEmailChange({
                  key: 'sendServerEmailToSlack',
                  value: val,
                });
              }}
            />
          </div>
        </div>
      </LoadingWrapper>
    </DefaultContentLayout>
  );
};

export default Advanced;
